import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import type { ApiWorkflowDialogMessages } from '@verantos-dev/shared-components';

import { useAutosaveStagedDefinition } from './useAutosaveStagedDefinition';
import { useVariablePickerOptions } from './useVariablePickerOptions';
import { selectSources } from '../state/selectors';
import { useAnalysisDefinitionStore } from '../state/useAnalysisDefinitionStore';
import type { UseAddVariablePickerModalArgs, UseAddVariablePickerModalResult } from '../types';
import { hasVariableSource } from '../utils/analysisSources';

export const useAddVariablePickerModal = ({
  projectId,
  onClose,
}: UseAddVariablePickerModalArgs): UseAddVariablePickerModalResult => {
  const intl = useIntl();
  const sources = useAnalysisDefinitionStore(selectSources);
  const { autosave, isSaving, saveError } = useAutosaveStagedDefinition(projectId);

  const {
    allOptions,
    options,
    searchQuery,
    onSearchChange,
    isLoading,
    isError,
    emptyMessage,
    noResultsMessage,
  } = useVariablePickerOptions(projectId);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const onSelect = useCallback(
    (analyticalVariableId: string) => setSelectedId(analyticalVariableId),
    []
  );

  // Saves the addition as soon as it is confirmed; the dialog closes once it lands.
  const onConfirm = useCallback(() => {
    if (!selectedId) return;
    if (hasVariableSource(sources ?? {}, selectedId)) {
      onClose();
      return;
    }
    const selectedOption = allOptions.find((option) => option.analyticalVariableId === selectedId);
    autosave(
      {
        kind: 'addVariable',
        avId: selectedId,
        description: selectedOption?.description ?? null,
      },
      onClose
    );
  }, [selectedId, sources, allOptions, autosave, onClose]);

  const canConfirm = selectedId !== null && !isLoading;

  const dialogMessages: ApiWorkflowDialogMessages = {
    entrySaveButtonText: intl.formatMessage({ id: 'analysisDetail.addVariableModal.addConfirm' }),
    entryCancelButtonText: intl.formatMessage({ id: 'analysisDetail.addVariableModal.cancel' }),
    submittingSaveButtonText: intl.formatMessage({ id: 'analysisDetail.addVariableModal.adding' }),
    submittingCancelButtonText: intl.formatMessage({
      id: 'analysisDetail.addVariableModal.cancel',
    }),
    submittingErrorTitle: intl.formatMessage({ id: 'analysisDetail.addVariableModal.errorTitle' }),
    submittingErrorText: intl.formatMessage({ id: 'analysisDetail.addVariableModal.errorText' }),
    submittingErrorSaveButtonText: intl.formatMessage({
      id: 'analysisDetail.addVariableModal.retry',
    }),
  };

  return {
    title: intl.formatMessage({ id: 'analysisDetail.addVariableModal.addTitle' }),
    selectHeading: intl.formatMessage(
      { id: 'analysisDetail.addVariableModal.selectHeading' },
      { count: options.length }
    ),
    searchPlaceholder: intl.formatMessage({
      id: 'analysisDetail.addVariableModal.searchPlaceholder',
    }),
    emptyMessage,
    noResultsMessage,
    searchQuery,
    onSearchChange,
    options,
    selectedId,
    onSelect,
    onConfirm,
    onCancelClose: onClose,
    isLoading,
    isError,
    canConfirm,
    isSubmitting: isSaving,
    submitError: saveError,
    dialogMessages,
  };
};
